

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, ArrowRight, Play } from 'lucide-react';

import VideoModal from './VideoModal';
import video from '../assets/video_mdx.mp4';
import imgTraining from '../assets/training.png';
import vidMDX1 from '../assets/vidMDX1.mp4';
import vidMDX2 from '../assets/vidMDX2.mp4';
import unoImg from '../assets/Uno.png';
import noUnoImg from '../assets/NoUno1.png';

const Hero = () => {
  const features = [
    "Industry Consultants",
    "24/7 Support",
    "75+ Years Experience"
  ];

  // Define los videos que deseas rotar
  const videos = [vidMDX1, vidMDX2];

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videos.length);
    }, 8000); // Cambia el video cada 8 segundos

    return () => clearInterval(interval); // Limpia el intervalo al desmontar
  }, [videos.length]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <section id="home" className="pt-40 relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-blue-500/20"></div>
        <video
          key={currentVideoIndex}
          className="absolute inset-0 w-full h-full object-cover opacity-20"
          src={videos[currentVideoIndex]}
          autoPlay
          loop
          muted
          playsInline
        />
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-orange-400/30 rounded-full"
            animate={{
              x: [0, 100, 0],
              y: [0, -100, 0],
              opacity: [0.3, 0.8, 0.3]
            }}
            transition={{
              duration: 4 + i,
              repeat: Infinity,
              delay: i * 0.5
            }}
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + i * 10}%`
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10  ">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ">
          {/* Content */}
          <motion.div
            className="text-white"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-3xl sm:text-5xl lg:text-3xl font-semibold mb-1 leading-tight grid grid-cols-3 gap-4 "
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="grid grid-rows-1 col-span-4 ">
                {/* <span className="lg:text-6xl font-bold">In the Safety Industry</span> */}
                <img src={noUnoImg} alt="Uno" className="w-[250px] h-auto " />
              </div>

            </motion.h1>
            <motion.h1
              className="text-3xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight "
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Workforce
              <span className="block bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                Consultants &
              </span>
              Trainers
            </motion.h1>

            <motion.p
              className="text-base sm:text-lg text-gray-300 mb-8 leading-relaxed max-w-full sm:max-w-lg "
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              At MDX workforce Academy, we deliver specialized workforce consulting and technical training solutions across three core divisions:
              <span className="block bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">Workforce Development, Industrial Safety, and TxDOT & Environmental.</span>
            </motion.p>

            {/* Features */}
            <motion.div
              className="flex flex-wrap gap-4 mb-8 "
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-gray-300 font-medium">{feature}</span>
                </div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <motion.button
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-full font-bold text-lg flex items-center gap-2 hover:shadow-2xl transition-all duration-300 w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <a href="mailto:info@mdxsafetyinc.com" className="flex items-center gap-2 text-white hover:text-[#a1a09a] transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span className="font-semibold">Get Started Today</span>
                </a>
                <ArrowRight className="w-5 h-5" />
              </motion.button>

              <motion.button
                onClick={openModal}
                className="border-2 border-white/30 text-white px-6 py-3 rounded-full font-bold text-lg flex items-center gap-2 hover:bg-white/10 transition-all duration-300 w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Play className="w-5 h-5" />
                Watch Video
              </motion.button>

              <VideoModal
                isOpen={isModalOpen}
                onClose={closeModal}
                videoSrc={video}
              />
            </motion.div>
          </motion.div>

          {/* Visual Element */}
          <motion.div
            className="relative w-full max-w-full sm:max-w-lg mx-auto "
            initial={{ opacity: 0, x: 80 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative">
              <motion.div
                className="w-[750px] sm:h-[500px] bg-gradient-to-br from-blue-500/20 to-blue-500/20 rounded-3xl backdrop-blur-sm border border-white/10"
                animate={{
                  rotateY: [0, 5, 0],
                  rotateX: [0, 2, 0]
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div className="absolute inset-6 bg-gradient-to-br from-white/10 to-white/5 rounded-2xl flex items-center justify-center">
                  <div
                    className="rounded-2xl absolute inset-0 opacity-80 bg-center bg-no-repeat bg-cover"
                    style={{
                      backgroundImage: `url(${imgTraining})`
                    }}
                  ></div>
                </div>
              </motion.div>

              {/* Floating Elements */}
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-500 to-red-400 rounded-2xl flex items-center justify-center"
                  animate={{
                    y: [0, -20, 0],
                    rotate: [0, 180, 360]
                  }}
                  transition={{
                    duration: 3 + i,
                    repeat: Infinity,
                    delay: i * 0.5
                  }}
                  style={{
                    top: `${20 + i * 30}%`,
                    right: `${-10 + i * 5}%`
                  }}
                >
                  <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>

  );
};

export default Hero;
